import { IBaseCoords, ILineConfig } from "../types";

export function getNearPointStraightLine(sp: IBaseCoords, ep: IBaseCoords, cp: IBaseCoords): IBaseCoords {
	const { x: X1, y: Y1 } = sp;
	const { x: X2, y: Y2 } = ep;
	const { x: X3, y: Y3 } = cp;

	if (X1 === X2)
		return {
			x: X1,
			y: Y3,
		};
	if (Y1 === Y2)
		return {
			x: X3,
			y: Y1,
		};

	const M1 = (Y2 - Y1) / (X2 - X1);
	const M2 = -1 / M1;

	const X = (M1 * X1 - M2 * X3 + Y3 - Y1) / (M1 - M2);
	const Y = M2 * (X - X3) + Y3;

	return {
		x: X,
		y: Y,
	};
}

export function getLineAngle(sp: IBaseCoords, ep: IBaseCoords): number {
	const { x: X1, y: Y1 } = sp;
	const { x: X2, y: Y2 } = ep;

	return Math.atan((Y2 - Y1) / (X2 - X1)) * (180 / Math.PI);
}

export function getLineLength(sp: IBaseCoords, ep: IBaseCoords): number {
	const { x: X1, y: Y1 } = sp;
	const { x: X2, y: Y2 } = ep;

	return Math.sqrt((X2 - X1) * (X2 - X1) + (Y2 - Y1) * (Y2 - Y1));
}

export function getLinePercentage(sp: IBaseCoords, ep: IBaseCoords, cp: IBaseCoords) {
	const fullLength = getLineLength(sp, ep);
	const partLength = getLineLength(sp, cp);

	return partLength <= fullLength ? partLength / fullLength : fullLength / partLength;
}

export function getDistanceFactor(distance = 50): number {
	if (typeof distance === "string") {
		distance = parseFloat(distance);
	}
	if (distance >= 100) return 1;
	if (distance <= 0) return 0;

	return distance / 100;
}

export function getDistancePercentage(factor: number): number {
	return factor * 100;
}

export function getDistanceEblowLine(line: ILineConfig, cp: IBaseCoords): number {
	const points = line.points;

	const nodeLength = [];
	let minDistance = +Infinity;
	let totalLength = 0;
	let tailLength = 0;
	let tailIndex = 0;

	for (let index = 0; index < points.length - 1; index++) {
		const sp = points[index];
		const ep = points[index + 1];

		const lp = getNearPointStraightLine(sp, ep, cp);

		const nearDistance = getLineLength(lp, cp);
		const lineLength = getLineLength(sp, ep);

		totalLength += lineLength;
		nodeLength.push(lineLength);

		let { x: X1, y: Y1 } = sp;
		let { x: X2, y: Y2 } = ep;
		const { x: X3, y: Y3 } = lp;

		if (X1 > X2) {
			[X1, X2] = [X2, X1];
		}
		if (Y1 > Y2) {
			[Y1, Y2] = [Y2, Y1];
		}

		const XColl = X2 >= X3 && X1 <= X3;
		const YColl = Y2 >= Y3 && Y1 <= Y3;

		if (nearDistance <= minDistance && XColl && YColl) {
			minDistance = nearDistance;

			tailLength = getLineLength(sp, lp);
			tailIndex = index;
		}
	}

	let currentLength = 0;

	if (tailIndex !== 0) {
		for (let index = 0; index < tailIndex; index++) {
			currentLength += nodeLength[index];
		}
	}
	currentLength += tailLength;

	return (currentLength / totalLength) * 100;
}
